// ABOUTME: Interviews browse page — coverage strip, filters (URL-backed), sortable table,
// ABOUTME: row actions (re-analyze / admin delete), corpus staleness banner, activity panel

import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowDown, ArrowUp, RotateCw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ActivityPanel } from '@/components/interviews/ActivityPanel'
import { CoverageStrip } from '@/components/interviews/CoverageStrip'
import { StatusBadge, deriveStatus } from '@/components/interviews/StatusBadge'
import {
  activeJobFor, latestJobFor, useDeleteInterview, useInterviews, useJobs,
  useReanalyzeBatch, useReanalyzeInterview,
} from '@/hooks/useInterviews'
import { usePersonas } from '@/hooks/usePersonas'
import { useAuth } from '@/contexts/AuthContext'
import { InterviewSummary } from '@/api/interviews'
import { Job } from '@/api/ingestion'

const INSTITUTIONS = ['ALX', 'ALU', 'ALA', 'FIT']
const MINUTES_PER_REANALYSIS = 2 // order-of-magnitude, for the batch confirm

type SortKey = 'date' | 'evidence_count'

function errText(err: any): string {
  const d = err?.response?.data?.detail
  return typeof d === 'string' ? d : (err?.message || 'Request failed')
}

export function InterviewsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [searchParams, setSearchParams] = useSearchParams()
  const group = searchParams.get('group')
  const institution = searchParams.get('institution')
  const q = searchParams.get('q') ?? ''
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortAsc, setSortAsc] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<InterviewSummary | null>(null)
  const [confirmBatch, setConfirmBatch] = useState<string[] | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [liveMessage, setLiveMessage] = useState('')

  const { data, isLoading, isError, refetch } = useInterviews({
    group: group ?? undefined,
    institution: institution ?? undefined,
    q: q || undefined,
  })
  // Unfiltered list drives the coverage strip counts, so filtering doesn't collapse the meter
  const allQuery = useInterviews({})
  const allData = allQuery.data
  const { data: personas } = usePersonas()
  const { data: jobs } = useJobs()

  const reanalyze = useReanalyzeInterview()
  const reanalyzeBatch = useReanalyzeBatch()
  const deleteInterview = useDeleteInterview()

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const groupTaxonomy = useMemo(() => {
    const fromDefinitions = (personas?.definitions ?? []).map((d) => d.persona_type)
    const fromData = (allData?.interviews ?? []).map((i) => i.group).filter((g): g is string => !!g)
    return [...new Set([...fromDefinitions, ...fromData])]
  }, [personas, allData])

  const groupCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const i of allData?.interviews ?? []) {
      if (i.group) counts[i.group] = (counts[i.group] ?? 0) + 1
    }
    return counts
  }, [allData])

  const sorted = useMemo(() => {
    const rows = [...(data?.interviews ?? [])]
    rows.sort((a, b) => {
      const av = sortKey === 'date' ? (a.date ?? '') : a.evidence_count
      const bv = sortKey === 'date' ? (b.date ?? '') : b.evidence_count
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sortAsc ? cmp : -cmp
    })
    return rows
  }, [data, sortKey, sortAsc])

  // Corpus staleness: CONTENT hash of the framework (ids+statements+signals) vs the
  // hash each interview was analyzed against — catches in-place hypothesis edits that
  // leave the count unchanged. Null stamp (pre-hash data) counts as stale.
  const currentHypothesisCount = data?.current_hypothesis_count ?? 0
  const currentFrameworkHash = data?.current_framework_hash ?? null
  const staleIds = useMemo(
    () => (allData?.interviews ?? [])
      .filter((i) => currentFrameworkHash != null &&
        (i.hypothesis_set_hash == null || i.hypothesis_set_hash !== currentFrameworkHash))
      .map((i) => i.interview_id),
    [allData, currentFrameworkHash],
  )
  const processingCount = (jobs ?? []).filter(
    (j) => j.kind === 'reanalyze' && (j.status === 'queued' || j.status === 'running'),
  ).length

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc)
    else {
      setSortKey(key)
      setSortAsc(false)
    }
  }

  const onReanalyzeRow = (row: InterviewSummary) => {
    setActionError(null)
    reanalyze.mutate(row.interview_id, {
      onSuccess: () => setLiveMessage(`Re-analysis queued for ${row.interviewee}`),
      onError: (err) => setActionError(errText(err)),
    })
  }

  const onConfirmDelete = () => {
    if (!confirmDelete) return
    setActionError(null)
    deleteInterview.mutate(confirmDelete.interview_id, {
      onSuccess: () => {
        setLiveMessage(`Deleted ${confirmDelete.interviewee}'s interview`)
        setConfirmDelete(null)
      },
      onError: (err) => {
        setActionError(errText(err))
        setConfirmDelete(null)
      },
    })
  }

  const onConfirmBatch = () => {
    if (!confirmBatch) return
    setActionError(null)
    reanalyzeBatch.mutate(confirmBatch, {
      onSuccess: (res) => {
        setLiveMessage(`Queued ${res.job_ids.length} re-analysis jobs`)
        setConfirmBatch(null)
      },
      onError: (err) => {
        setActionError(errText(err))
        setConfirmBatch(null)
      },
    })
  }

  const hasFilters = !!(group || institution || q)
  const isFirstRun =
    !isLoading && !isError && allQuery.isSuccess && allQuery.data.interviews.length === 0

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Screen-reader announcements for job/action state changes */}
      <div aria-live="polite" className="sr-only">{liveMessage}</div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-alx-blue">Interviews</h1>
        <Button onClick={() => navigate('/dashboard/interviews/ingest')}>Ingest interview</Button>
      </div>

      {actionError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 flex items-center justify-between">
          <p className="text-sm text-red-700">{actionError}</p>
          <button className="text-sm text-red-700 underline" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-6">
          <div>
            <Skeleton className="h-3 w-64 mb-2" />
            <div className="flex gap-1.5">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-8 flex-1" />
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">Couldn't load interviews.</p>
          <Button variant="outline" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : isFirstRun ? (
        <div className="space-y-6">
          <CoverageStrip groups={groupTaxonomy} counts={{}} total={0} activeGroup={null} onSelect={() => {}} />
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-10 text-center">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              0 of {groupTaxonomy.length || 10} groups covered
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              The listening tour starts here — ingest the first interview and it will appear in this list,
              evaluated against every hypothesis.
            </p>
            <Button onClick={() => navigate('/dashboard/interviews/ingest')}>
              Ingest your first interview
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <CoverageStrip
            groups={groupTaxonomy}
            counts={groupCounts}
            total={allData?.interviews.length ?? 0}
            activeGroup={group}
            onSelect={(g) => setParam('group', g)}
          />

          {staleIds.length > 0 && (
            <div className="rounded-lg border border-alx-gold bg-alx-gold/10 px-4 py-3 flex items-center justify-between gap-4">
              <p className="text-sm text-alx-blue">
                {processingCount > 0
                  ? `Re-analyzing — ${processingCount} interview${processingCount === 1 ? '' : 's'} remaining in the queue.`
                  : `Analysis may be out of date: ${staleIds.length} of ${allData?.interviews.length ?? 0} interviews were analyzed against an older version of the hypotheses (now ${currentHypothesisCount} hypotheses).`}
              </p>
              {isAdmin && processingCount === 0 && (
                <Button variant="outline" onClick={() => setConfirmBatch(staleIds)}>
                  Re-analyze all stale
                </Button>
              )}
            </div>
          )}

          {/* Group filtering lives on the coverage strip — no duplicate dropdown */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={institution ?? ''}
              onChange={(e) => setParam('institution', e.target.value || null)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-alx-green/40"
              aria-label="Filter by institution"
            >
              <option value="">All institutions</option>
              {INSTITUTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <Input
              value={q}
              onChange={(e) => setParam('q', e.target.value || null)}
              placeholder="Search interviews…"
              className="max-w-xs"
              aria-label="Search interviews"
            />
          </div>

          {sorted.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
              <p className="text-sm text-gray-600 mb-3">No interviews match.</p>
              {hasFilters && (
                <Button variant="outline" onClick={() => setSearchParams({}, { replace: true })}>
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-3">Interviewee</th>
                    <th className="px-4 py-3">Group</th>
                    <th className="px-4 py-3">Institution</th>
                    <SortableTh
                      label="Date"
                      active={sortKey === 'date'}
                      asc={sortAsc}
                      onClick={() => toggleSort('date')}
                    />
                    <SortableTh
                      label="Evidence"
                      active={sortKey === 'evidence_count'}
                      asc={sortAsc}
                      onClick={() => toggleSort('evidence_count')}
                      alignRight
                    />
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row) => (
                    <InterviewRow
                      key={row.interview_id}
                      row={row}
                      jobs={jobs}
                      isAdmin={isAdmin}
                      onReanalyze={() => onReanalyzeRow(row)}
                      onDelete={() => setConfirmDelete(row)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <ActivityPanel jobs={jobs} />
        </div>
      )}

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {confirmDelete?.interviewee}'s interview?</DialogTitle>
            <DialogDescription>
              This removes {confirmDelete?.evidence_count ?? 0} evidence item
              {(confirmDelete?.evidence_count ?? 0) === 1 ? '' : 's'} and{' '}
              {confirmDelete?.question_count ?? 0} Q&A pairs from the graph and cannot be undone.
              You can re-ingest it later from the original transcript.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={onConfirmDelete} disabled={deleteInterview.isPending}>
              {deleteInterview.isPending ? 'Deleting…' : 'Delete interview'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch re-analyze confirm */}
      <Dialog open={!!confirmBatch} onOpenChange={(open) => !open && setConfirmBatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-analyze {confirmBatch?.length} interview{(confirmBatch?.length ?? 0) === 1 ? '' : 's'}?</DialogTitle>
            <DialogDescription>
              Each runs the full extraction pipeline against the current {currentHypothesisCount} hypotheses
              — roughly {MINUTES_PER_REANALYSIS} min each ≈ {Math.max(1, Math.round((confirmBatch?.length ?? 0) * MINUTES_PER_REANALYSIS))} min total.
              They update one by one; you can keep working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBatch(null)}>Cancel</Button>
            <Button onClick={onConfirmBatch} disabled={reanalyzeBatch.isPending}>
              {reanalyzeBatch.isPending ? 'Queueing…' : 'Re-analyze all'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTh({ label, active, asc, onClick, alignRight }: {
  label: string; active: boolean; asc: boolean; onClick: () => void; alignRight?: boolean
}) {
  return (
    <th className={`px-4 py-3 ${alignRight ? 'text-right' : ''}`} aria-sort={active ? (asc ? 'ascending' : 'descending') : undefined}>
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-gray-800 ${active ? 'text-gray-800' : ''}`}
      >
        {label}
        {active && (asc ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
      </button>
    </th>
  )
}

function InterviewRow({ row, jobs, isAdmin, onReanalyze, onDelete }: {
  row: InterviewSummary
  jobs: Job[] | undefined
  isAdmin: boolean
  onReanalyze: () => void
  onDelete: () => void
}) {
  const activeJob = activeJobFor(jobs, row.interview_id)
  const latestJob = latestJobFor(jobs, row.interview_id)
  const status = deriveStatus(row, activeJob, latestJob)

  return (
    <tr className="border-b border-gray-100 last:border-0 hover:bg-alx-green/5 transition-colors">
      <td className="px-4 py-3">
        <Link
          to={`/dashboard/interviews/${row.interview_id}`}
          className="font-medium text-alx-blue hover:underline"
        >
          {row.interviewee}
        </Link>
        {row.name && <span className="block text-xs text-gray-500">{row.name}</span>}
      </td>
      <td className="px-4 py-3 text-gray-700">{row.group ?? '—'}</td>
      <td className="px-4 py-3 text-gray-700">{row.institution ?? '—'}</td>
      <td className="px-4 py-3 text-gray-700">{row.date ?? '—'}</td>
      <td className="px-4 py-3 text-right text-gray-700">{row.evidence_count}</td>
      <td className="px-4 py-3">
        <StatusBadge status={status} error={latestJob?.status === 'failed' ? latestJob.error : null} />
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  size="icon" variant="ghost"
                  aria-label={`Re-analyze ${row.interviewee}'s interview`}
                  disabled={!!activeJob}
                  onClick={onReanalyze}
                >
                  <RotateCw className="w-4 h-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {activeJob ? 'A job is already running for this interview' : 'Re-analyze against the current hypotheses'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {isAdmin && (
          <Button
            size="icon" variant="ghost"
            aria-label={`Delete ${row.interviewee}'s interview`}
            disabled={!!activeJob}
            onClick={onDelete}
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        )}
      </td>
    </tr>
  )
}

